import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthData } from '../../interfaces/auth';
import { StorageName } from '../../util/constants';
import { AuthService } from '../auth';
import { API_HOSTS } from '../http/apiHosts';
import { identifyMonitoredUser, reportSessionEvent } from '../sentry';
import { classifyFailure } from './classifyFailure';

export type SessionVerdict = 'alive' | 'dead' | 'unknown';
/** `not-due`: no expiry, not reached yet, or not this tab's session to judge. */
export type ExpiryCheck = 'not-due' | 'alive' | 'unknown' | 'expired';

type Listener = () => void;
type Teardown = () => void | Promise<void>;

/** The session this tab signed in with, or found in storage when it loaded or adopted one. */
export interface LoadedSession {
  token: string | null;
  userId: string | null;
  expiresAtMs: number | null;
  /** When this browser signed the session in, by its own clock; null when unknown. */
  startedAtMs: number | null;
  source: string | null;
}

const NO_SESSION: LoadedSession = {
  token: null,
  userId: null,
  expiresAtMs: null,
  startedAtMs: null,
  source: null,
};

/** Set on each instrumented request, so a failure can be traced to the session that sent it. */
const GENERATION_TAG = '__sessionGeneration';
type TaggedConfig = AxiosRequestConfig & { [GENERATION_TAG]?: number };

/**
 * Enforcement switch. With this off the monitor still classifies failures and
 * reports its verdicts to Sentry, but never signs anyone out — so the triage
 * rules can be validated against real traffic before a false positive can log
 * out every user at once.
 */
const ENFORCE = process.env.REACT_APP_SESSION_GUARD_ENFORCE === 'true';

/** Floor between probes, so a burst of failures cannot become a burst of probes. */
const MIN_PROBE_INTERVAL_MS = 15_000;
/** How long a verdict stays fresh enough to skip re-probing on navigation. */
const STALE_RECHECK_MS = 5 * 60_000;
/** First re-check of an expiry Core did not confirm; later ones wait STALE_RECHECK_MS. */
const EXPIRY_RECHECK_MS = 60_000;
/** Escalating gaps after an inconclusive probe, for when Core is simply down. */
const UNKNOWN_BACKOFF_MS = [15_000, 30_000, 60_000, 300_000];
const PROBE_TIMEOUT_MS = 8_000;
/** Every sign-out awaits the teardowns, so one that never settles must not hold it up. */
const TEARDOWN_BUDGET_MS = 3_000;
/** The longest session the Hub mints (its legacy-session maximum). */
const MAX_SESSION_MS = 12 * 60 * 60_000;
/**
 * Grace past the later of the expiry and MAX_SESSION_MS of local elapsed time,
 * after which the session is treated as over whatever Core says. See `ceilingAt`.
 */
const EXPIRY_CEILING_MS = 15 * 60_000;
/** setTimeout's ceiling (~24.8 days); a longer delay would fire at once. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * A bare instance with **no** interceptor attached.
 *
 * This is what makes the monitor structurally loop-safe: a failing probe cannot
 * re-enter `reportSuspiciousFailure`, because nothing is watching this client.
 * Do not pass it through `installAuthInterceptor`.
 */
const probeClient = axios.create({ timeout: PROBE_TIMEOUT_MS });

let inFlight: Promise<SessionVerdict> | null = null;
let lastProbeAt = 0;
let lastVerdict: SessionVerdict = 'alive';
let consecutiveUnknown = 0;
let dead = false;
let reportedDeath = false;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let loaded: LoadedSession = NO_SESSION;
let expiryRechecks = 0;
let teardownRun: Promise<void> | null = null;
let reportedUnconfirmedExpiry = false;
let reportedCeiling = false;
/** When Core last accepted the session past its expiry; paces checkAuth's re-checks. */
let lastAliveAfterExpiryAt = 0;
let lastExpiryCheck: Promise<void> | null = null;
/** Bumped whenever this tab moves to another session, so a probe of the old one is ignored. */
let generation = 0;

const deadListeners = new Set<Listener>();
const teardowns = new Set<Teardown>();

const readStoredToken = (): string | null => {
  try {
    return AuthService.getAuth()?.auth_token || null;
  } catch (error) {
    // An unreadable credential is no credential
    return null;
  }
};

/** Whether the tab still holds `token`, the session it asked about at `askedIn`. */
const stillSameSession = (token: string | null, askedIn: number): boolean =>
  token !== null && askedIn === generation && loaded.token === token && readStoredToken() === token;

const readStoredUserId = (): string | null => {
  try {
    const userId = AuthService.getAuth()?.user_id;
    return userId == null ? null : String(userId);
  } catch (error) {
    return null;
  }
};

const currentGate = () =>
  consecutiveUnknown > 0
    ? UNKNOWN_BACKOFF_MS[Math.min(consecutiveUnknown - 1, UNKNOWN_BACKOFF_MS.length - 1)]
    : MIN_PROBE_INTERVAL_MS;

/**
 * The single, idempotent "the session is over" trigger.
 *
 * `dead` is a one-way latch, so however many requests fail at once, listeners
 * fire exactly once per page life. That is what guarantees a single logout
 * without the React layer needing to coordinate.
 */
const markDead = (reason: string, detail: Record<string, unknown> = {}) => {
  if (dead) {
    return;
  }

  if (!reportedDeath) {
    reportedDeath = true;
    try {
      reportSessionEvent('Session detected as expired', {
        reason,
        enforced: ENFORCE,
        ...detail,
      });
    } catch (error) {
      // A reporting failure must not stop the session from ending
    }
  }

  if (!ENFORCE) {
    // Report-only: do not latch and do not notify, so behaviour is unchanged.
    return;
  }

  dead = true;
  lastVerdict = 'dead';
  deadListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      // One bad listener must not stop the others from running.
    }
  });
};

interface ProbeOutcome {
  verdict: SessionVerdict;
  unknownStreak: 'reset' | 'increment' | 'keep';
}

/** Asks the server; `reason` is what a confirmed death is reported as. */
const verify = (reason: string, detail: Record<string, unknown>): Promise<SessionVerdict> => {
  if (dead) {
    return Promise.resolve('dead');
  }

  const authToken = readStoredToken();
  if (!authToken) {
    // Nothing to verify. Not treated as an expiry: react-admin's own checkAuth
    // already handles "no credentials" by redirecting to login.
    return Promise.resolve('unknown');
  }
  if (authToken !== loaded.token) {
    // Another tab's session this tab has not taken on yet: not this tab's to judge
    return Promise.resolve('unknown');
  }

  if (inFlight) {
    return inFlight;
  }

  const elapsed = Date.now() - lastProbeAt;
  if (lastProbeAt !== 0 && elapsed < currentGate()) {
    return Promise.resolve(lastVerdict);
  }

  const probeGeneration = generation;
  // Wrapped so a synchronous throw is an inconclusive probe, not an exception
  const probe = new Promise<AxiosResponse>((resolve) =>
    resolve(
      probeClient.get(`${API_HOSTS.appointments}/session`, {
        headers: { Authorization: `Bearer ${authToken}` },
      }),
    ),
  )
    .then(
      (response): ProbeOutcome => ({
        verdict: response.status === 200 ? 'alive' : 'unknown',
        unknownStreak: 'reset',
      }),
    )
    .catch((error: AxiosError): ProbeOutcome => {
      if (error?.response?.status === 401) {
        return { verdict: 'dead', unknownStreak: 'keep' };
      }
      // Everything else — no response, 5xx, 429 — is inconclusive. Never `dead`:
      // an unreachable backend must not sign people out.
      return { verdict: 'unknown', unknownStreak: 'increment' };
    })
    .then(({ verdict, unknownStreak }): SessionVerdict => {
      // A verdict on a session this tab has since left, or that another tab has
      // replaced, says nothing about the current one
      if (!stillSameSession(authToken, probeGeneration)) {
        return 'unknown';
      }
      if (unknownStreak === 'reset') {
        consecutiveUnknown = 0;
      } else if (unknownStreak === 'increment') {
        consecutiveUnknown += 1;
      }
      lastVerdict = verdict;
      if (verdict === 'dead') {
        markDead(reason, detail);
      }
      return verdict;
    })
    .finally(() => {
      if (probeGeneration === generation) {
        inFlight = null;
        lastProbeAt = Date.now();
      }
    });

  inFlight = probe;
  return probe;
};

const verifySession = (): Promise<SessionVerdict> =>
  verify('probe-rejected', { status: 401, host: 'appointments' });

/**
 * Whether a failed request was sent for the session this tab holds now. A request
 * sent with an earlier session's token (before an adopt, or by a client that kept
 * an old token) must not end the current one.
 *
 * Two checks, because not every request carries its token the same way: most send
 * it as a `Bearer` or `Token` Authorization header, which is compared whole, but
 * some put it in the URL (`services/common.ts`), so those are traced by the session
 * generation stamped on them when they were sent. A request with neither (not sent
 * through an instrumented client) counts as current.
 */
const isFromLoadedSession = (error: unknown): boolean => {
  if (!loaded.token) {
    return false;
  }
  const config = (error as AxiosError | undefined)?.config as TaggedConfig | undefined;
  const headers = (config?.headers ?? {}) as Record<string, unknown>;
  const authorization = headers.Authorization ?? headers.authorization;
  if (typeof authorization === 'string') {
    return authorization === `Bearer ${loaded.token}` || authorization === `Token ${loaded.token}`;
  }
  const sentIn = config?.[GENERATION_TAG];
  return typeof sentIn === 'number' ? sentIn === generation : true;
};

/** Stamps a request with the session it is sent for; see `isFromLoadedSession`. */
const tagRequest = <T extends AxiosRequestConfig>(config: T): T => {
  (config as TaggedConfig)[GENERATION_TAG] = generation;
  return config;
};

/**
 * Ends the session on a request the server rejected, unless that request was sent
 * for another session. Returns whether it was about this tab's session.
 */
const markRequestRejected = (
  error: unknown,
  reason: string,
  detail: Record<string, unknown> = {},
): boolean => {
  if (!isFromLoadedSession(error)) {
    return false;
  }
  markDead(reason, detail);
  return true;
};

const reportSuspiciousFailure = (error: AxiosError): void => {
  if (dead) {
    return;
  }

  // Not signed in: there is no session to lose. This also keeps failed sign-in
  // attempts from being read as expiries.
  if (!readStoredToken()) {
    return;
  }

  const { suspicion, host, status, code } = classifyFailure(error);

  if (suspicion === 'certain') {
    markRequestRejected(error, 'rejected-request', { status, host, code });
    return;
  }

  if (suspicion === 'probe') {
    void verifySession();
  }
};

/** Fire-and-forget re-check, for route changes and window focus. */
const verifyIfStale = (): void => {
  if (dead || inFlight) {
    return;
  }
  if (lastProbeAt !== 0 && Date.now() - lastProbeAt < STALE_RECHECK_MS) {
    return;
  }
  if (!readStoredToken()) {
    return;
  }
  void verifySession();
};

/**
 * Runs once per session: the guard tears down as soon as a dead session is safe
 * to, and every logout makes sure it has, so later calls share the first run.
 */
const runTeardowns = (): Promise<void> => {
  if (!teardownRun) {
    const pending = Array.from(teardowns);
    let budget: ReturnType<typeof setTimeout> | undefined;
    const settled = Promise.all(
      pending.map(async (teardown) => {
        try {
          await teardown();
        } catch (error) {
          // Teardown is best-effort; a failure here must not block the logout.
        }
      }),
    ).then(() => undefined);
    const outOfTime = new Promise<void>((resolve) => {
      budget = setTimeout(resolve, TEARDOWN_BUDGET_MS);
    });
    teardownRun = Promise.race([settled, outOfTime]).finally(() => clearTimeout(budget));
  }
  return teardownRun;
};

const clearExpiryTimer = (): void => {
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
};

const parseExpiry = (value: string | null | undefined): number | null => {
  const expiresAtMs = value ? Date.parse(value) : NaN;
  return Number.isNaN(expiresAtMs) ? null : expiresAtMs;
};

const readStoredExpiry = (): number | null =>
  parseExpiry(localStorage.getItem(StorageName.AUTH_EXPIRES_AT));

const armExpiry = (expiresAtMs: number): void => {
  clearExpiryTimer();
  expiryRechecks = 0;
  const delay = expiresAtMs - Date.now();
  if (delay > MAX_TIMER_MS) {
    expiryTimer = setTimeout(() => armExpiry(expiresAtMs), MAX_TIMER_MS);
    return;
  }
  expiryTimer = setTimeout(() => fireExpiry(expiresAtMs), Math.max(delay, 0));
};

/** A timer callback has no caller to throw to. */
const fireExpiry = (armedExpiryMs: number): void => {
  try {
    onExpiryTimer(armedExpiryMs);
  } catch (error) {
    // Nothing to recover: the next request, probe or navigation re-checks the session
  }
};

/**
 * When a session past its expiry is treated as over whatever Core says:
 * EXPIRY_CEILING_MS past the later of its expiry and MAX_SESSION_MS after this
 * browser signed it in. Anchored to the local sign-in time, so a fast clock (even a
 * day ahead) cannot cut or loop a fresh session; a slow or rolled-back clock
 * lengthens it instead. A best-effort client-side bound on how long an expired
 * session stays on screen, not a security control: ending sessions is Core's and
 * the Hub's job. Null without a local sign-in time, which disables it.
 */
const ceilingAt = (expiresAtMs: number): number | null =>
  loaded.startedAtMs === null
    ? null
    : Math.max(expiresAtMs, loaded.startedAtMs + MAX_SESSION_MS) + EXPIRY_CEILING_MS;

const reportCeilingOnce = (): void => {
  if (reportedCeiling) {
    return;
  }
  reportedCeiling = true;
  try {
    reportSessionEvent('Session detected as expired', {
      reason: 'expiry-ceiling',
      code: lastVerdict,
      enforced: ENFORCE,
    });
  } catch (error) {
    // Reporting is best-effort
  }
};

/**
 * The whole past-expiry policy, shared by checkAuth and the expiry timer. Only for
 * the session this tab owns, and never rejects.
 *
 * The expiry is a server timestamp read against the client's clock, so a fast
 * clock would end a live session, and once enforced loop at sign-in. Core has the
 * last word: only its 401 ends the session (reason `expired`), and only if this
 * tab still holds the session it asked about. Past `ceilingAt` the session ends
 * regardless (reason `expiry-ceiling`) when enforcing; report-only only reports it.
 *
 * Navigation calls this often, so once Core has accepted the session past its
 * expiry the answer is reused for STALE_RECHECK_MS; `fresh` skips that, for the
 * timer's own, already paced, re-checks.
 */
const checkExpiry = async ({ fresh = false }: { fresh?: boolean } = {}): Promise<ExpiryCheck> => {
  try {
    if (dead) {
      return 'expired';
    }
    const token = loaded.token;
    const expiresAtMs = readStoredExpiry();
    if (!token || !ownsStoredSession() || expiresAtMs === null || expiresAtMs > Date.now()) {
      return 'not-due';
    }
    const ceiling = ceilingAt(expiresAtMs);
    if (ceiling !== null && Date.now() >= ceiling) {
      if (ENFORCE) {
        markDead('expiry-ceiling', { code: lastVerdict });
        return 'expired';
      }
      // Report-only: never ends access on the client clock; Core still decides below
      reportCeilingOnce();
    }
    if (
      !fresh &&
      lastVerdict === 'alive' &&
      lastAliveAfterExpiryAt !== 0 &&
      Date.now() - lastAliveAfterExpiryAt < STALE_RECHECK_MS
    ) {
      return 'alive';
    }

    const checking = generation;
    const verdict = await verify('expired', { status: 401, host: 'appointments' });
    if (!stillSameSession(token, checking)) {
      return 'unknown';
    }
    if (verdict === 'dead') {
      // A no-op unless the verdict came from a probe started for another reason
      markDead('expired', { status: 401, host: 'appointments' });
      return 'expired';
    }
    if (verdict === 'alive') {
      lastAliveAfterExpiryAt = Date.now();
    }
    return verdict;
  } catch (error) {
    return 'unknown';
  }
};

/**
 * The timer's side of `checkExpiry`: an expiry Core did not confirm is reported
 * once and re-checked, first after a minute and then at the stale-check interval,
 * up to the ceiling. Never rejects.
 */
const confirmExpiry = async (armedExpiryMs: number): Promise<void> => {
  try {
    const token = loaded.token;
    const confirming = generation;
    const result = await checkExpiry({ fresh: true });
    // Ended, or re-armed, adopted or signed out while the probe was out
    if (
      result === 'expired' ||
      result === 'not-due' ||
      !stillSameSession(token, confirming) ||
      expiryTimer
    ) {
      return;
    }
    const ceiling = ceilingAt(armedExpiryMs);
    const untilCeiling = ceiling === null ? Infinity : ceiling - Date.now();
    // Past the ceiling only in report-only mode: nothing left to find out
    if (untilCeiling > 0) {
      const delay = Math.min(
        expiryRechecks === 0 ? EXPIRY_RECHECK_MS : STALE_RECHECK_MS,
        untilCeiling,
      );
      expiryRechecks += 1;
      expiryTimer = setTimeout(() => fireExpiry(armedExpiryMs), delay);
    }
    if (!reportedUnconfirmedExpiry) {
      reportedUnconfirmedExpiry = true;
      reportSessionEvent('Session expiry not confirmed by Core', {
        reason: 'expiry-unconfirmed',
        code: result,
        enforced: ENFORCE,
      });
    }
  } catch (error) {
    // Fire-and-forget from a timer: nobody to reject to
  }
};

/**
 * Another tab may have signed out, signed in again or moved the expiry since this
 * timer was armed, so storage is re-read before anything is concluded: a stale
 * tab must never end (or report on) a newer session.
 */
const onExpiryTimer = (armedExpiryMs: number): void => {
  expiryTimer = null;
  if (dead) {
    return;
  }
  const storedToken = readStoredToken();
  if (!storedToken) {
    // Signed out elsewhere; the storage listener has already ended this tab's session
    return;
  }
  if (storedToken !== loaded.token) {
    adoptStoredSession();
    return;
  }
  const storedExpiryMs = readStoredExpiry();
  if (storedExpiryMs === null) {
    loaded = { ...loaded, expiresAtMs: null };
    return;
  }
  if (storedExpiryMs !== armedExpiryMs || storedExpiryMs > Date.now()) {
    loaded = { ...loaded, expiresAtMs: storedExpiryMs };
    armExpiry(storedExpiryMs);
    return;
  }
  lastExpiryCheck = confirmExpiry(armedExpiryMs);
};

/** Forgets everything this tab knows about the session, without touching storage. */
const resetLocalState = (): void => {
  clearExpiryTimer();
  generation += 1;
  loaded = NO_SESSION;
  reportedUnconfirmedExpiry = false;
  reportedCeiling = false;
  lastAliveAfterExpiryAt = 0;
  teardownRun = null;
  dead = false;
  reportedDeath = false;
  inFlight = null;
  lastProbeAt = 0;
  lastVerdict = 'alive';
  consecutiveUnknown = 0;
};

const identifyStoredUser = (): void => {
  try {
    const userId = Number(AuthService.getAuth()?.user_id);
    if (!Number.isFinite(userId)) {
      return;
    }
    // Profile is written after the credential, so it may still be the previous user's
    const profile = AuthService.getLocalProfile();
    identifyMonitoredUser(userId, String(profile?.id) === String(userId) ? profile.email : '');
  } catch (error) {
    // Attribution is best-effort
  }
};

/** Takes the session in storage as this tab's own, re-arming its expiry. */
const loadStoredSession = (): void => {
  const token = readStoredToken();
  let expiresAtMs = readStoredExpiry();
  if (expiresAtMs === null && localStorage.getItem(StorageName.AUTH_EXPIRES_AT) !== null) {
    // Garbage would otherwise sit next to every later session
    localStorage.removeItem(StorageName.AUTH_EXPIRES_AT);
  }
  const startedAtMs = parseExpiry(localStorage.getItem(StorageName.AUTH_STARTED_AT));
  if (startedAtMs === null && localStorage.getItem(StorageName.AUTH_STARTED_AT) !== null) {
    // Treated as no local anchor: the ceiling stays off until the next sign-in
    localStorage.removeItem(StorageName.AUTH_STARTED_AT);
  }
  if (!token) {
    expiresAtMs = null;
  }
  loaded = {
    token,
    userId: token ? readStoredUserId() : null,
    expiresAtMs,
    startedAtMs: token ? startedAtMs : null,
    source: localStorage.getItem(StorageName.AUTH_SOURCE),
  };
  if (expiresAtMs !== null) {
    armExpiry(expiresAtMs);
  }
};

/**
 * Switches this tab to the session another tab signed in with. Clears the dead
 * latch: the verdict was about the session this tab is leaving. A session of
 * another user reloads the app, so nothing loaded for the previous user stays on
 * screen.
 */
const adoptStoredSession = (): void => {
  const previousUserId = loaded.userId;
  resetLocalState();
  loadStoredSession();
  identifyStoredUser();
  if (previousUserId !== null && loaded.userId !== null && loaded.userId !== previousUserId) {
    // The phone device goes offline first, so it is not left registered for the previous user
    void runTeardowns().finally(() => window.location.assign('/'));
  }
};

/** Whether the credential in storage is the one this tab signed in with or loaded. */
const ownsStoredSession = (): boolean => {
  const storedToken = readStoredToken();
  return storedToken !== null && storedToken === loaded.token;
};

/**
 * Starts monitoring a freshly signed-in session: clears any latched verdict from
 * the previous one, stores the credential with where it came from and when it
 * expires, and arms the expiry timer.
 *
 * A Hub-minted Core session has a fixed end, so the timer fires the same "session
 * is over" path as a rejected request instead of waiting for one. The expiry is
 * persisted next to the credential so a reload re-arms it; an empty or
 * unparseable value clears it.
 *
 * Other tabs get one storage event per key, in write order. The start time, expiry
 * and source are written before the credential, so a tab that sees the new credential already
 * reads the new expiry, rather than adopting it with the previous session's.
 */
const startSession = ({
  auth,
  expiresAt,
  source,
}: {
  auth: AuthData;
  expiresAt?: string | null;
  source?: string | null;
}): void => {
  resetLocalState();
  const startedAtMs = Date.now();
  localStorage.setItem(StorageName.AUTH_STARTED_AT, new Date(startedAtMs).toISOString());
  const expiresAtMs = parseExpiry(expiresAt);
  if (expiresAtMs === null) {
    localStorage.removeItem(StorageName.AUTH_EXPIRES_AT);
  } else {
    localStorage.setItem(StorageName.AUTH_EXPIRES_AT, new Date(expiresAtMs).toISOString());
  }
  if (source) {
    localStorage.setItem(StorageName.AUTH_SOURCE, source);
  } else {
    localStorage.removeItem(StorageName.AUTH_SOURCE);
  }
  AuthService.setAuth(auth);
  loaded = {
    token: auth.auth_token,
    userId: auth.user_id == null ? null : String(auth.user_id),
    expiresAtMs,
    startedAtMs,
    source: source || null,
  };
  if (expiresAtMs !== null) {
    armExpiry(expiresAtMs);
  }
};

/** Ends this tab's session: called on sign-out. Also forgets the stored expiry and start time. */
const reset = (): void => {
  resetLocalState();
  localStorage.removeItem(StorageName.AUTH_EXPIRES_AT);
  localStorage.removeItem(StorageName.AUTH_STARTED_AT);
};

/**
 * Keeps this tab in step with the others. Events arrive one per key, and storage
 * may already hold later writes, so both keys are re-read on every event.
 */
const onStorage = (event: StorageEvent): void => {
  const { key } = event;
  // A null key is localStorage.clear()
  if (key !== null && key !== StorageName.AUTH && key !== StorageName.AUTH_EXPIRES_AT) {
    return;
  }
  const storedToken = readStoredToken();
  if (!storedToken) {
    // Without this, signing out in one tab leaves every other tab still showing
    // itself as an available phone agent, so inbound calls keep being routed to a
    // client that can no longer answer them.
    if (key !== StorageName.AUTH_EXPIRES_AT && loaded.token) {
      markDead('logged-out-in-another-tab');
    }
    return;
  }
  if (dead) {
    // Keeps showing "session expired"; the sign-out adopts whatever is stored then
    return;
  }
  if (storedToken !== loaded.token) {
    adoptStoredSession();
    return;
  }
  const storedExpiryMs = readStoredExpiry();
  if (storedExpiryMs === loaded.expiresAtMs) {
    return;
  }
  loaded = { ...loaded, expiresAtMs: storedExpiryMs };
  if (storedExpiryMs === null) {
    clearExpiryTimer();
  } else {
    armExpiry(storedExpiryMs);
  }
};

const installCrossTabListener = (): (() => void) => {
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};

let detachCrossTabListener = (): void => undefined;

export const sessionMonitor = {
  reportSuspiciousFailure,
  markRequestRejected,
  tagRequest,
  verifySession,
  verifyIfStale,
  runTeardowns,
  startSession,
  reset,
  ownsStoredSession,
  adoptStoredSession,
  checkExpiry: (): Promise<ExpiryCheck> => checkExpiry(),
  readStoredToken,
  getLoadedSession: (): LoadedSession => ({ ...loaded }),
  isSessionDead: () => dead,
  isEnforcing: () => ENFORCE,
  // Unsubscribers return void, not Set.delete's boolean, so they can be returned
  // directly from a useEffect.
  onSessionDead(listener: Listener): () => void {
    deadListeners.add(listener);
    return () => {
      deadListeners.delete(listener);
    };
  },
  registerTeardown(teardown: Teardown): () => void {
    teardowns.add(teardown);
    return () => {
      teardowns.delete(teardown);
    };
  },
  /**
   * Only tests call this directly; the app ends a session through a rejected
   * request, a probe, the expiry checks or another tab's sign-out.
   */
  markDead,
  /** Test-only: the expiry timer's last check, which is otherwise fire-and-forget. */
  lastExpiryCheckForTests: () => lastExpiryCheck,
  /** For tests: each isolated re-import would otherwise leave its listener behind. */
  detachCrossTabListener: () => detachCrossTabListener(),
};

if (typeof window !== 'undefined') {
  detachCrossTabListener = installCrossTabListener();

  // A reload must not drop the expiry of the session it restores
  loadStoredSession();
}
